import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Stack, Typography } from "@mui/material";
import { useFormik } from "formik";
import moment from "moment";
import { useEffect, useState, useRef, useMemo } from "react";
import "react-datepicker/dist/react-datepicker.css";
import { GiConsoleController, GiHamburgerMenu } from "react-icons/gi";
import "react-pro-sidebar/dist/css/styles.css";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import LoadingSpinner from "../../../components/LoadingSpinner";
import MultipleSelect from "../../../components/MultipleSelect";
import MonthCard from "../../../components/ExperienceLog/MonthCard";
import MonthCardSupervised from "../../../components/ExperienceLog/MonthCardSupervised";
import MonthCardConcentrated from "../../../components/ExperienceLog/MonthCardConcentrated";
import { fetchMonthlyCheck } from "../../../redux/sagas/experienceLog/fetchMonthlyCheckSaga";
import Tooltip from "../../../components/Tooltip";
import {
  setSuccess,
  useErrors,
  useFormData,
  useIsLoading,
  useMonthlyChecks,
  useSuccess,
} from "../../../redux/reducers/ExpLogReducer";
import {
  setShowSidebar,
  useShowSidebar,
} from "../../../redux/reducers/SidebarReducer";
import {
  fetchStaticData,
  taskModelVersionChanged,
} from "../../../redux/sagas/experienceLog/formDataSaga";
import { submitExpLog } from "../../../redux/sagas/experienceLog/submitFormSaga";
import {
  GROUP_HELP,
  INDEPENDENT_HELP,
  OBSERVATION_HELP,
  RESTRICTED_HELP,
  UNRESTRICTED_HELP,
  EXPERIENCE_TYPE_HELP,
  SETTING_HELP,
  SUPERVISOR_HELP,
  DATE_OF_EXPERIENCE_HELP,
  TIME_IN_HELP,
  TIME_OUT_HELP,
  SUP_START_HELP,
  SUP_END_HELP,
  TASK_LIST_ITEM_HELP,
  METHOD_OF_SUP_HELP,
  EXPERIENCE_HRS_HELP,
  SUPERVISION_CONTACT_HELP,
  SUMMARY_HELP,
  INDEPENDENT_HOURS_HELP,
  SUMMARY_HELP_ARRAY,
  LOGIN_CONFIRMATION_MESSAGE,
  V6_COMPULSORY_MESSAGE,
} from "../../../utils/CONSTANTS";
import { useDispatchEffect } from "../../../utils/hooks";
import { validateLogHourForm } from "../../../utils/validation";
import { useNavigate } from "react-router-dom";
import "./logHour.scss";
import React from "react";
import { useSupervisors } from "../../../redux/reducers/ProfileReducer";
import {
  fetchSupervisors,
  submitTraineeSupervisor,
} from "../../../redux/sagas/profile/supervisorSaga";
import ApiService from "../../../services/ApiService";
import CustomModal from "../../../components/CustomModal/CustomModal";
import Confirmation from "../../../components/Confirmation/Confirmation";
import LocalStorage from "../../../services/LocalStorage";
import ConfirmationPopup from "../../../components/ConfirmationPopup/ConfirmationPopup";
import {
  setTaskModelVersion,
  useIsV6,
} from "../../../redux/reducers/AppStateReducer";

const LogHour = () => {
  const navigate = useNavigate();
  const data = useFormData();
  const isLoading = useIsLoading();
  const isV6 = useIsV6();
  let { expTypes, setting, obsevations, methods, contacts, tasks } = data;
  const inputRef = useRef(null);
  const prevIsV6Ref = useRef();
  const supervisors = useSupervisors();
  const [addNewSupervisor, setAddNewSupervisor] = useState(false);
  const taskNames = tasks?.map((t) => t.name.split(" ")[0]);
  const taskDescriptions = tasks?.map((t) => t.name.split(/^(\D*\d+)(.*)$/)[2]);
  const taskIds = tasks?.map((t) => t.id);

  useDispatchEffect(fetchStaticData, null, expTypes?.length == 0);
  const refreshSupervisors = useDispatchEffect(fetchSupervisors);

  const [modalVisible, setModalVisible] = useState(false);
  const [loginModalVisible, setLoginModalVisible] = useState(false);
  const [v6CompulsoryModalVisible, setV6CompulsoryModalVisible] =
    useState(false);

  const initialValues = {
    expereince_type: 0,
    setting: 0,
    method_of_supervision: "",
    client_observation: "",
    supervision_contact: 0,
    task: [],
    supervisor: 0,
    newSupervisor: "",
    newSupervisorEmail: "",
    date_of_experience: moment().format("YYYY-MM-DD"),
    time_of_expereince_start: moment().format("HH:mm"),
    time_of_expereince_end: "",
    unrestricted_hours: 0,
    restricted_hours: 0,
    experience_hours: 0,
    individual_or_group: "",
    supervision_start_time: "",
    supervision_end_time: "",
    supervised_hours: 0,
    independant_hours: 0,
    experience_note: "",
  };

  const dispatch = useDispatch();
  const loginPopup = LocalStorage.getData("loginPopup");

  useEffect(() => {
    if (loginPopup) {
      setLoginModalVisible(true);
      LocalStorage.delete("loginPopup");
    }
  }, []);

  useEffect(() => {
    const prevIsV6 = prevIsV6Ref.current;

    if (!isV6 || (prevIsV6 === true && isV6 === false)) {
      setV6CompulsoryModalVisible(true);
      dispatch(setTaskModelVersion("v6"));
      dispatch(taskModelVersionChanged());
    }

    prevIsV6Ref.current = isV6;
  }, [isV6]);

  const calculateHours = (values) => {
    let results = {
      rawExperienceHours: 0,
      sumExpHours: 0,
      sumSupHours: 0,
      sumResHours: 0,
      sumIndHours: 0,
      finalExperienceHours: 0,
    };

    // Helper function to check if a value is valid
    const isValidValue = (val) => {
      return val !== undefined && val !== null && val !== "";
    };

    // Calculate experience hours from time range
    if (
      isValidValue(values.time_of_expereince_start) &&
      isValidValue(values.time_of_expereince_end)
    ) {
      const startTime = moment(values.time_of_expereince_start, "HH:mm");
      const endTime = moment(values.time_of_expereince_end, "HH:mm");

      if (startTime.isValid() && endTime.isValid()) {
        const duration = moment.duration(endTime.diff(startTime));
        const hours = duration.hours();
        const minutes = duration.minutes();
        results.sumExpHours = hours + minutes / 60;
        results.rawExperienceHours = results.sumExpHours;
      }
    }

    // Calculate supervised hours from time range
    if (
      isValidValue(values.supervision_start_time) &&
      isValidValue(values.supervision_end_time)
    ) {
      const startTime = moment(values.supervision_start_time, "HH:mm");
      const endTime = moment(values.supervision_end_time, "HH:mm");

      if (startTime.isValid() && endTime.isValid()) {
        const duration = moment.duration(endTime.diff(startTime));
        const hours = duration.hours();
        const minutes = duration.minutes();
        results.sumSupHours = hours + minutes / 60;
      }
    }

    // Calculate restricted hours
    if (
      isValidValue(results.rawExperienceHours) &&
      isValidValue(values.unrestricted_hours)
    ) {
      results.sumResHours =
        Number(results.rawExperienceHours) - Number(values.unrestricted_hours);
    }

    // Calculate independent hours
    if (
      isValidValue(results.rawExperienceHours) &&
      isValidValue(results.sumSupHours)
    ) {
      results.sumIndHours =
        Number(results.rawExperienceHours) - Number(results.sumSupHours);
    }

    // Calculate final experience hours with multiplier if needed
    if (isValidValue(results.rawExperienceHours)) {
      results.finalExperienceHours =
        values.expereince_type === "2"
          ? (Number(results.rawExperienceHours) * 1.33).toFixed?.(3)
          : Number(results.rawExperienceHours);
    }

    return results;
  };

  const createNewSupervisorForTrainee = async (
    supervisor,
    newSupervisorEmail
  ) => {
    let name = supervisor;
    let lastname = "";
    if (supervisor.indexOf(" ") > 0) {
      name = supervisor.split(" ")[0];
      const lastnameArr = supervisor.split(" ");
      lastnameArr.shift();
      lastname = lastnameArr.join(" ");
    }

    const form = {
      name,
      lastname,
      email: newSupervisorEmail,
    };

    try {
      const createdUser = await ApiService.postTraineeSupervisor(form);
      refreshSupervisors();
      setAddNewSupervisor(false);
      return createdUser.data.id;
    } catch (e) {
      console.log(e);
      return null;
    }
  };

  const onSubmit = async (values, onSubmitProps) => {
    const supervisor = JSON.parse(values.supervisor);

    let createdSupervisorId = null;
    if (addNewSupervisor) {
      createdSupervisorId = await createNewSupervisorForTrainee(
        values.newSupervisor,
        values.newSupervisorEmail
      );
    }

    const calculated = calculateHours(values);

    const newValues = {
      ...values,
      experience_hours: calculated.finalExperienceHours,
      restricted_hours: calculated.sumResHours,
      supervised_hours: calculated.sumSupHours,
      independant_hours: calculated.sumIndHours,
      task: values.task.map((t) => taskIds[taskNames.indexOf(t)]),
    };

    newValues["supervisor"] = supervisor?.supervisor?.user || null;
    newValues["trainee_supervisor"] = supervisor?.id;

    if (addNewSupervisor) {
      newValues["supervisor"] = null;
      newValues["trainee_supervisor"] = createdSupervisorId;
    }

    if (values.individual_or_group == "No supervision") {
      newValues["method_of_supervision"] = null;
      newValues["supervision_start_time"] = null;
      newValues["supervision_end_time"] = null;
      newValues["supervised_hours"] = null;
      newValues["independant_hours"] = null;
      newValues["client_observation"] = null;
      newValues["supervision_contact"] = null;
    }

    dispatch(submitExpLog(newValues));
  };

  const formik = useFormik({
    initialValues,
    onSubmit,
    validate: (values) => validateLogHourForm(values, { addNewSupervisor }),
  });

  // Calculate derived values whenever relevant form values change
  const calculatedValues = useMemo(() => {
    return calculateHours(formik.values);
  }, [
    formik.values.time_of_expereince_start,
    formik.values.time_of_expereince_end,
    formik.values.supervision_start_time,
    formik.values.supervision_end_time,
    formik.values.unrestricted_hours,
    formik.values.expereince_type,
  ]);

  const logCreateSuccess = useSuccess();
  const logCreateErrors = useErrors();

  useEffect(() => {
    if (logCreateSuccess) {
      setModalVisible(true);
      dispatch(setSuccess(false));
      formik.resetForm();
    }
  }, [logCreateSuccess]);

  useEffect(() => {
    if (logCreateErrors && Array.isArray(logCreateErrors))
      logCreateErrors.forEach((err) => {
        toast.error(err);
      });
  }, [logCreateErrors]);

  const showSidebar = useShowSidebar();

  if (isLoading) return <LoadingSpinner style={{ width: "100%" }} />;

  const handleClickedTooltipData = () => {
    const mappedArray = SUMMARY_HELP_ARRAY?.map((data) => {
      const result = `${data}\nAns:-\n`;
      return result;
    });

    inputRef.current.value = mappedArray.join("");
  };

  return (
    <div className="section">
      <div className="columns ">
        <div className="column">
          <button
            className="Toggle_btn"
            onClick={() => dispatch(setShowSidebar(!showSidebar))}
          >
            {showSidebar ? <GiHamburgerMenu /> : <GiHamburgerMenu />}
          </button>

          <p
            className="headingMain headMain"
            style={{ overflowWrap: "break-word" }}
          >
            Log Experience Hours
          </p>
          <div className="loghourDiv">
            <form onSubmit={formik.handleSubmit} className="logHourForm">
              <div className="column mt30 is-five-fifths logForm">
                <label className="label">
                  Experience Type <Tooltip content={EXPERIENCE_TYPE_HELP} />
                </label>
                <select
                  name="expereince_type"
                  className="select dropdownCustom mt is-normal"
                  value={formik.values.expereince_type}
                  onChange={formik.handleChange}
                >
                  <option value={0} disabled>
                    Select
                  </option>
                  {expTypes.map((item) => (
                    <option value={Number(item.id)} key={item.id}>
                      {item.name}
                    </option>
                  ))}
                </select>
                <span className="Register__error">
                  {(formik.touched.expereince_type &&
                    formik.errors.expereince_type) ||
                    ""}
                </span>

                <label className="label">
                  Setting <Tooltip content={SETTING_HELP} />
                </label>
                <select
                  name="setting"
                  className="select dropdownCustom mt is-normal"
                  value={formik.values.setting}
                  onChange={formik.handleChange}
                >
                  <option value={0} disabled>
                    Select
                  </option>
                  {setting.map((item) => (
                    <option value={Number(item.id)} key={item.id}>
                      {item.name}
                    </option>
                  ))}
                </select>
                <span className="Register__error">
                  {(formik.touched.setting && formik.errors.setting) || ""}
                </span>

                <label className="label justify-between log__supervisor">
                  <div>
                    Supervisor <Tooltip content={SUPERVISOR_HELP} />
                  </div>
                  <div>
                    <button
                      onClick={() => setAddNewSupervisor(!addNewSupervisor)}
                      type="button"
                    >
                      {!addNewSupervisor ? "+ Add new" : "Select"}
                    </button>
                  </div>
                </label>
                {!addNewSupervisor ? (
                  <select
                    name="supervisor"
                    className="select dropdownCustom mt is-normal"
                    value={formik.values.supervisor}
                    onChange={formik.handleChange}
                  >
                    <option value={0} disabled>
                      Select
                    </option>
                    {supervisors.map((item) => (
                      <option value={JSON.stringify(item)} key={item.id}>
                        {item.name + " " + item.lastname}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    className="input is-small"
                    type="text"
                    placeholder="New Supervisor Name"
                    name="newSupervisor"
                    value={formik.values.newSupervisor}
                    onChange={formik.handleChange}
                  />
                )}
                {addNewSupervisor ? (
                  <span className="Register__error">
                    {(formik.touched.newSupervisor &&
                      formik.errors.newSupervisor) ||
                      ""}
                  </span>
                ) : (
                  <span className="Register__error">
                    {(formik.touched.supervisor && formik.errors.supervisor) ||
                      ""}
                  </span>
                )}

                {addNewSupervisor && (
                  <>
                    <label className="label justify-between log__supervisor">
                      Supervisor Email
                    </label>
                    <input
                      className="input is-small"
                      type="text"
                      placeholder="New Supervisor Email"
                      name="newSupervisorEmail"
                      value={formik.values.newSupervisorEmail}
                      onChange={formik.handleChange}
                    />
                    <span className="Register__error">
                      {(formik.touched.newSupervisorEmail &&
                        formik.errors.newSupervisorEmail) ||
                        ""}
                    </span>
                  </>
                )}

                <label className="label">
                  Date of Experience{" "}
                  <Tooltip content={DATE_OF_EXPERIENCE_HELP} />
                </label>
                <input
                  className="input is-small"
                  type="date"
                  placeholder={formik.values.date_of_experience}
                  name="date_of_experience"
                  value={formik.values.date_of_experience}
                  onChange={formik.handleChange}
                />
                <span className="Register__error">
                  {(formik.touched.date_of_experience &&
                    formik.errors.date_of_experience) ||
                    ""}
                </span>

                <label className="label">
                  Time of Experience Start <Tooltip content={TIME_IN_HELP} />
                </label>
                <input
                  className="input is-small"
                  type="time"
                  placeholder=" "
                  name="time_of_expereince_start"
                  value={formik.values.time_of_expereince_start}
                  onChange={formik.handleChange}
                />
                <span className="Register__error">
                  {(formik.touched.time_of_expereince_start &&
                    formik.errors.time_of_expereince_start) ||
                    ""}
                </span>
                <label className="label">
                  Time of Experience End <Tooltip content={TIME_OUT_HELP} />
                </label>
                <input
                  className="input is-small"
                  type="time"
                  placeholder=" "
                  name="time_of_expereince_end"
                  value={formik.values.time_of_expereince_end}
                  onChange={formik.handleChange}
                />
                <span className="Register__error">
                  {(formik.touched.time_of_expereince_end &&
                    formik.errors.time_of_expereince_end) ||
                    ""}
                </span>

                <label className="label">
                  Unrestricted Hours <Tooltip content={UNRESTRICTED_HELP} />
                </label>
                <input
                  className="input is-small"
                  type="number"
                  placeholder="Type here ..."
                  name="unrestricted_hours"
                  value={formik.values.unrestricted_hours}
                  onChange={formik.handleChange}
                  step="0.25"
                  min="0"
                />
                <span className="Register__error">
                  {(formik.touched.unrestricted_hours &&
                    formik.errors.unrestricted_hours) ||
                    ""}
                </span>

                <label className="label">
                  Restricted Hours <Tooltip content={RESTRICTED_HELP} />
                </label>
                <input
                  className="input is-small"
                  type="number"
                  placeholder="Type here ..."
                  name="restricted_hours"
                  value={calculatedValues.sumResHours.toFixed?.(2)}
                  readOnly
                />
                <span className="Register__error">
                  {(formik.touched.restricted_hours &&
                    formik.errors.restricted_hours) ||
                    ""}
                </span>

                <label className="label">
                  Experience Hours
                  <Tooltip content={EXPERIENCE_HRS_HELP} />
                </label>
                <input
                  className="input is-small"
                  type="number"
                  placeholder="Type here ..."
                  name="raw_experience_hours"
                  value={calculatedValues.rawExperienceHours.toFixed?.(2)}
                  readOnly
                />
                <span className="Register__error">
                  {(formik.touched.experience_hours &&
                    formik.errors.experience_hours) ||
                    ""}
                </span>

                <div style={{ display: "none" }}>
                  <label className="label">
                    Final Experience Hours
                    <Tooltip content={EXPERIENCE_HRS_HELP} />
                  </label>
                  <input
                    className="input is-small"
                    type="number"
                    placeholder="Type here ..."
                    name="experience_hours"
                    value={calculatedValues.finalExperienceHours?.toFixed?.(2)}
                    readOnly
                  />
                </div>

                <label className="label">
                  Individual <Tooltip content={INDEPENDENT_HELP} /> or group
                  supervision <Tooltip content={GROUP_HELP} /> ?
                </label>
                <select
                  name="individual_or_group"
                  className="select dropdownCustom mt is-normal"
                  value={formik.values.individual_or_group}
                  onChange={formik.handleChange}
                >
                  <option value={""} disabled>
                    Select
                  </option>
                  <option value="Individual">Individual</option>
                  <option value="Group">Group</option>
                  <option value="No supervision">No supervision</option>
                </select>
                <span className="Register__error">
                  {(formik.touched.individual_or_group &&
                    formik.errors.individual_or_group) ||
                    ""}
                </span>

                {formik.values.individual_or_group ===
                "No supervision" ? null : (
                  <>
                    <label className="label">
                      Method of Supervision
                      <Tooltip content={METHOD_OF_SUP_HELP} />
                    </label>
                    <select
                      name="method_of_supervision"
                      className="select dropdownCustom mt is-normal"
                      value={formik.values.method_of_supervision}
                      onChange={formik.handleChange}
                    >
                      <option value={""} disabled>
                        Select
                      </option>
                      <option value="In Person" key="In Person">
                        In Person
                      </option>
                      <option value="Virtual" key="Virtual">
                        Virtual
                      </option>
                    </select>
                    <span className="Register__error">
                      {(formik.touched.method_of_supervision &&
                        formik.errors.method_of_supervision) ||
                        ""}
                    </span>

                    <label className="label">
                      Supervision Start Time
                      <Tooltip content={SUP_START_HELP} />
                    </label>
                    <input
                      className="input is-small"
                      type="time"
                      placeholder=" "
                      name="supervision_start_time"
                      value={formik.values.supervision_start_time}
                      onChange={formik.handleChange}
                    />
                    <span className="Register__error">
                      {(formik.touched.supervision_start_time &&
                        formik.errors.supervision_end_time) ||
                        ""}
                    </span>

                    <label className="label">
                      Supervision End Time <Tooltip content={SUP_END_HELP} />
                    </label>
                    <input
                      className="input is-small"
                      type="time"
                      placeholder=" "
                      name="supervision_end_time"
                      value={formik.values.supervision_end_time}
                      onChange={formik.handleChange}
                    />
                    <span className="Register__error">
                      {(formik.touched.supervision_end_time &&
                        formik.errors.supervision_end_time) ||
                        ""}
                    </span>

                    <label className="label">Supervised Hours </label>
                    <input
                      className="input is-small"
                      type="number"
                      placeholder="Type here ..."
                      name="supervised_hours"
                      value={calculatedValues.sumSupHours?.toFixed?.(2)}
                      readOnly
                    />
                    <span className="Register__error">
                      {(formik.touched.supervised_hours &&
                        formik.errors.supervised_hours) ||
                        ""}
                    </span>

                    <label className="label">
                      Independent Hours
                      <Tooltip content={INDEPENDENT_HOURS_HELP} />{" "}
                    </label>
                    <input
                      className="input is-small"
                      type="number"
                      placeholder="Type here ..."
                      name="independant_hours"
                      value={calculatedValues.sumIndHours?.toFixed?.(2)}
                      readOnly
                    />
                    <span className="Register__error">
                      {(formik.touched.independant_hours &&
                        formik.errors.independant_hours) ||
                        ""}
                    </span>

                    <label className="label">
                      Client Observation?
                      <Tooltip content={OBSERVATION_HELP} />
                    </label>
                    <select
                      name="client_observation"
                      className="select dropdownCustom mt is-normal"
                      value={formik.values.client_observation}
                      onChange={formik.handleChange}
                    >
                      <option value={""} disabled>
                        Select
                      </option>
                      <option value="Yes" key="yes">
                        Yes
                      </option>
                      <option value="No" key="no">
                        No
                      </option>
                    </select>
                    <span className="Register__error">
                      {(formik.touched.client_observation &&
                        formik.errors.client_observation) ||
                        ""}
                    </span>

                    <label className="label">
                      Supervision contacts{" "}
                      <Tooltip content={SUPERVISION_CONTACT_HELP} />
                    </label>
                    <select
                      name="supervision_contact"
                      className="select dropdownCustom mt is-normal"
                      value={formik.values.supervision_contact}
                      onChange={formik.handleChange}
                    >
                      <option value={0} disabled>
                        Select
                      </option>
                      {contacts.map((item) => (
                        <option value={Number(item.id)} key={item.id}>
                          {item.contact}
                        </option>
                      ))}
                    </select>
                    <span className="Register__error">
                      {(formik.touched.supervision_contact &&
                        formik.errors.supervision_contact) ||
                        ""}
                    </span>
                  </>
                )}
                <label className="label">
                  {isV6
                    ? "Test Content Outline documented"
                    : "Task list items documented"}
                  <Tooltip content={TASK_LIST_ITEM_HELP} />
                </label>
                <MultipleSelect
                  name="task"
                  value={formik.values.task}
                  handleChange={formik.handleChange}
                  items={taskNames}
                  description={taskDescriptions}
                />

                <>
                  <span className="Register__error">
                    {(formik.touched.task && formik.errors.task) || ""}
                  </span>

                  <label className="label justify-between log__supervisor">
                    <div>
                      Summary of Activity
                      <Tooltip content={SUMMARY_HELP} />
                    </div>
                    <div>
                      <button onClick={handleClickedTooltipData} type="button">
                        Copy Template
                      </button>
                    </div>
                  </label>
                  <textarea
                    className="textarea"
                    placeholder="Type here..."
                    name="experience_note"
                    ref={inputRef}
                    value={formik.values.experience_note}
                    onChange={formik.handleChange}
                  ></textarea>
                  <span className="Register__error">
                    {(formik.touched.experience_note &&
                      formik.errors.experience_note) ||
                      ""}
                  </span>
                </>

                <button
                  className="button is-two-fifths is-large is-rounded dashBtn mt30"
                  type="submit"
                >
                  Submit
                </button>
              </div>
            </form>
            {formik.values.expereince_type === 0 ? (
              <MonthCard
                values={formik.values}
                expHours={calculatedValues.rawExperienceHours?.toFixed?.(2)}
              />
            ) : null}
            {formik.values.expereince_type === "1" ? (
              <MonthCardSupervised
                values={formik.values}
                expHours={calculatedValues.rawExperienceHours?.toFixed?.(2)}
              />
            ) : null}
            {formik.values.expereince_type === "2" ? (
              <MonthCardConcentrated
                values={formik.values}
                expHours={calculatedValues.rawExperienceHours?.toFixed?.(2)}
              />
            ) : null}
          </div>
        </div>
      </div>

      <CustomModal
        modalVisible={modalVisible}
        onClose={setModalVisible}
        maxWidth="450px"
      >
        <Confirmation setModalVisible={setModalVisible} />
      </CustomModal>

      <CustomModal
        modalVisible={loginModalVisible}
        onClose={setLoginModalVisible}
        maxWidth="450px"
      >
        <ConfirmationPopup
          setModalVisible={setLoginModalVisible}
          message={LOGIN_CONFIRMATION_MESSAGE}
        />
      </CustomModal>

      <CustomModal
        modalVisible={v6CompulsoryModalVisible}
        onClose={setV6CompulsoryModalVisible}
        maxWidth="450px"
      >
        <ConfirmationPopup
          setModalVisible={setV6CompulsoryModalVisible}
          message={V6_COMPULSORY_MESSAGE}
        />
      </CustomModal>
    </div>
  );
};

export default LogHour;
